import { useMemo, useState } from 'react'
import { ActionIcon, Alert, Button, Checkbox, Loader } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconChevronDown, IconChevronRight, IconUsersGroup } from '@tabler/icons-react'
import {
  useAddPopulationCohorts,
  usePopulationCohortCount,
  usePopulationCohorts,
  type Campaign,
  type PopulationCohortRef,
  type PopulationCohortTree,
} from './hooks'
import { CrmModal } from './CrmModal'
import { CORAL, CrmIconTile, CrmSearchInput } from './CrmUi'
import { errorMessage } from './labels'

type Group = PopulationCohortTree['groups'][number]
type Family = Group['families'][number]
type Measure = Family['measures'][number]

/** A cohort as the tree shows it: its level, its counts, and the cohorts under it. */
interface Node {
  ref: PopulationCohortRef
  key: string
  label: string
  patients: number
  highRisk: number
  children: Node[]
}

const keyOf = (ref: PopulationCohortRef) => `${ref.type}:${ref.slug}`

const toNode = (
  type: PopulationCohortRef['type'],
  cohort: Group | Family | Measure,
  children: Node[],
): Node => ({
  ref: { type, slug: cohort.slug },
  key: keyOf({ type, slug: cohort.slug }),
  label: cohort.label,
  patients: cohort.patients,
  highRisk: cohort.highRisk,
  children,
})

const treeOf = (data: PopulationCohortTree): Node[] =>
  data.groups.map((group) =>
    toNode(
      'group',
      group,
      group.families.map((family) =>
        toNode(
          'family',
          family,
          family.measures.map((measure) => toNode('measure', measure, [])),
        ),
      ),
    ),
  )

const patientsText = (n: number) => `${n} ${n === 1 ? 'patient' : 'patients'}`

/** "Added 194 patients · 18 left out: no mobile number · 12 were already in". */
const addedMessage = (result: { added: number; leftOut: number; alreadyIn: number }) =>
  [
    `Added ${patientsText(result.added)}`,
    result.leftOut > 0 && `· ${result.leftOut} left out: no mobile number`,
    result.alreadyIn > 0 && `· ${result.alreadyIn} were already in`,
  ]
    .filter(Boolean)
    .join(' ')

const INDENT = ['pl-0', 'pl-7', 'pl-14']

/**
 * Adds Population Health cohorts to a draft's recipients (PD-1993, docs/CRM.md): the tree of
 * groups, conditions and measures with today's counts, searchable; tick one or several, and
 * every patient open in any of them — each once — joins the campaign. Ticking a cohort covers
 * the cohorts under it. Mounted while open, so each opening starts fresh.
 */
export function PopulationCohortPicker({
  campaign,
  onClose,
}: {
  campaign: Campaign
  onClose: () => void
}) {
  const cohorts = usePopulationCohorts(true)
  const add = useAddPopulationCohorts()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Node[]>([])
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set())
  const [openFamilies, setOpenFamilies] = useState<Set<string>>(new Set())

  const tree = useMemo(() => (cohorts.data ? treeOf(cohorts.data) : []), [cohorts.data])
  const refs = selected.map((node) => node.ref)
  const count = usePopulationCohortCount(refs)
  // Kept data is the previous selection's: treat it as still counting, never as this one's.
  const patients =
    refs.length && !count.isPlaceholderData && !count.isError ? count.data?.patients : undefined
  const max = count.data?.max
  const overCap = patients !== undefined && max !== undefined && patients > max

  const needle = search.trim().toLowerCase()
  const matches = (node: Node): boolean =>
    node.label.toLowerCase().includes(needle) || node.children.some(matches)

  const isSelected = (node: Node) => selected.some((s) => s.key === node.key)
  const toggle = (node: Node) =>
    setSelected((current) => {
      if (current.some((s) => s.key === node.key)) return current.filter((s) => s.key !== node.key)
      // A ticked cohort covers the ones under it: keep only the top-most.
      const under = new Set<string>()
      const walk = (n: Node) => n.children.forEach((c) => (under.add(c.key), walk(c)))
      walk(node)
      return [...current.filter((s) => !under.has(s.key)), node]
    })

  const flip = (set: Set<string>, key: string) => {
    const next = new Set(set)
    if (!next.delete(key)) next.add(key)
    return next
  }

  const renderNode = (node: Node, depth: number, covered: boolean, parentMatched: boolean) => {
    const selfMatched = !needle || parentMatched || node.label.toLowerCase().includes(needle)
    if (!selfMatched && !node.children.some(matches)) return null
    const expanded = needle
      ? true
      : node.ref.type === 'group'
        ? !collapsedGroups.has(node.key)
        : openFamilies.has(node.key)
    const checked = covered || isSelected(node)
    const childWord = node.ref.type === 'group' ? 'conditions' : 'measures'
    return (
      <li key={node.key}>
        <div
          className={`flex min-h-[44px] items-center gap-2 rounded-lg pr-2 hover:bg-[#F7F8FA] ${INDENT[depth]}`}
        >
          {node.children.length > 0 ? (
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              aria-label={`${expanded ? 'Hide' : 'Show'} ${node.label} ${childWord}`}
              aria-expanded={expanded}
              disabled={Boolean(needle)}
              onClick={() =>
                node.ref.type === 'group'
                  ? setCollapsedGroups((s) => flip(s, node.key))
                  : setOpenFamilies((s) => flip(s, node.key))
              }
            >
              {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </ActionIcon>
          ) : (
            <span className="w-[22px] shrink-0" />
          )}
          <Checkbox
            label={node.label}
            checked={checked}
            disabled={covered || node.patients === 0}
            onChange={() => toggle(node)}
            color={CORAL}
            radius="sm"
            className="min-w-0 flex-1"
            styles={{
              label: {
                fontWeight: depth === 0 ? 700 : depth === 1 ? 600 : 500,
                color: node.patients === 0 ? '#9AA1AC' : '#1F2733',
              },
            }}
          />
          {node.highRisk > 0 && (
            <span className="rounded-full bg-[#FDECEA] px-2 py-0.5 text-xs font-semibold whitespace-nowrap text-[#B42318]">
              {node.highRisk} high risk
            </span>
          )}
          <span className="w-[96px] shrink-0 text-right text-sm text-[#4A5160] tabular-nums">
            {patientsText(node.patients)}
          </span>
        </div>
        {expanded && node.children.length > 0 && (
          <ul>
            {node.children.map((child) =>
              renderNode(
                child,
                depth + 1,
                checked,
                parentMatched || (selfMatched && Boolean(needle)),
              ),
            )}
          </ul>
        )}
      </li>
    )
  }

  const run = () =>
    add.mutate(
      { campaignId: campaign.id, cohorts: refs },
      {
        onSuccess: (result) => {
          notifications.show({
            color: result.added ? 'green' : 'gray',
            message: addedMessage(result),
          })
          onClose()
        },
        onError: (error) =>
          notifications.show({
            color: 'red',
            message: errorMessage(error, 'Couldn’t add the cohorts'),
          }),
      },
    )

  const shown = tree.map((group) => renderNode(group, 0, false, false)).filter(Boolean)

  return (
    <CrmModal
      opened
      onClose={onClose}
      size={720}
      // Top-aligned: search shrinks the tree, and a centred dialog would move under the cursor.
      centered={false}
      title="Population cohorts"
      icon={
        <CrmIconTile>
          <IconUsersGroup size={20} />
        </CrmIconTile>
      }
      subtitle="Patients with an open care gap or risk today"
      footer={
        <>
          <div className="mr-auto flex min-w-0 flex-col text-sm">
            {selected.length === 0 ? (
              <span className="text-[#6B7280]">Pick one or more cohorts</span>
            ) : (
              <>
                <span className="truncate font-semibold text-[#1F2733]">
                  {selected.map((node) => node.label).join(', ')}
                </span>
                <span className={overCap || count.isError ? 'text-[#B42318]' : 'text-[#6B7280]'}>
                  {overCap
                    ? `That's ${patients} patients; a campaign can take up to 5,000 at once`
                    : count.isError
                      ? 'Couldn’t count these cohorts. Try again in a moment.'
                      : patients === undefined
                        ? 'Counting…'
                        : `${patientsText(patients)}, each counted once`}
                </span>
              </>
            )}
          </div>
          <Button variant="default" radius="md" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color={CORAL}
            radius="md"
            fw={700}
            disabled={patients === undefined || patients === 0 || overCap}
            loading={add.isPending}
            onClick={run}
          >
            {patients === undefined ? 'Add patients' : `Add ${patientsText(patients)}`}
          </Button>
        </>
      }
    >
      <CrmSearchInput
        label="Search cohorts"
        placeholder="Search groups, conditions and measures"
        value={search}
        onChange={setSearch}
      />
      {cohorts.isPending ? (
        <Loader size="sm" aria-label="Loading Population cohorts" />
      ) : cohorts.isError ? (
        <Alert color="red" radius="md">
          Couldn’t load Population cohorts. Try again in a moment.
        </Alert>
      ) : shown.length === 0 ? (
        <p className="text-sm text-[#6B7280]">
          {needle ? `No cohorts match “${search.trim()}”.` : 'Population has no cohorts yet.'}
        </p>
      ) : (
        <ul aria-label="Population cohorts" className="flex flex-col">
          {shown}
        </ul>
      )}
    </CrmModal>
  )
}
